import Store from '@ember-data/store';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import ContactModel, {
  type ContactPhoneEmailItem,
} from 'donor-elf-web/models/contact';
import MailchimpEmailSyncModel from 'donor-elf-web/models/mailchimp-email-sync';
import AbilitiesService from 'donor-elf-web/services/abilities';
import Settings from 'donor-elf-web/services/settings';
import { Changeset } from 'ember-changeset';

interface ComponentArgs {
  contact: ContactModel;
  contactDetailRecordIsLoading: boolean;
  // it's expected this will always be blank when the arguments are initially passed
  // and then updated once `contactDetailRecordIsLoading` is true
  mailchimpEmailSyncs: MailchimpEmailSyncModel[];
}

interface EmailItem {
  label?: string;
  data: ContactPhoneEmailItem;
  dataIndex: number;
  mailchimpEmailSync?: MailchimpEmailSyncModel;
}

interface EmailItemChangeset {
  get(property: 'label' | 'value' | 'syncWithMailchimp'): any;
  set(property: 'label' | 'value' | 'syncWithMailchimp'): void;
  execute(): void;
}

export default class ContactDetailsEmailsComponent extends Component<ComponentArgs> {
  constructor(owner: any, args: ComponentArgs) {
    super(owner, args);
    this.contactEmailsData = this.args.contact.emails?.data || [];
  }

  @service declare abilities: AbilitiesService;
  @service declare settings: Settings;
  @service declare store: Store;

  @tracked editing = false;
  @tracked saving = false;
  @tracked waitingForRecordToLoad = false;
  // creating tracked property instead of just using args.mailchimpEmailSyncs
  // because it could be changed after the emails are saved
  @tracked mailchimpEmailSyncs: MailchimpEmailSyncModel[] = [];
  // creating tracked property instead of just using args.contacts.emails
  // because it could be changed after the emails are saved
  @tracked contactEmailsData: ContactPhoneEmailItem[] = [];

  @tracked changesets: EmailItemChangeset[] = [];
  @tracked saveError?: any;

  get fundIsSyncedToMailchimp() {
    return this.settings.hasMailchimpFundSync;
  }

  // when going from one contact record to the next, this component is re-used
  // so we need to call then when the @contact record changes because the same
  // component instance is being reused
  @action
  reset() {
    this.editing = false;
    this.saving = false;
    this.waitingForRecordToLoad = false;
    this.mailchimpEmailSyncs = [];
    this.changesets = [];
    this.saveError = undefined;
    this.contactEmailsData = this.args.contact.emails?.data || [];
  }

  @action
  handleArgsContactDetailRecordIsLoadingUpdated() {
    if (
      this.waitingForRecordToLoad &&
      this.args.contactDetailRecordIsLoading === false
    ) {
      this.waitingForRecordToLoad = false;
      this.startEditing();
    }
  }

  @action
  handleArgsMailchimpEmailSyncsUpdated() {
    this.mailchimpEmailSyncs = this.args.mailchimpEmailSyncs.slice();
  }

  get emailItems(): EmailItem[] {
    let mailchimpEmailSyncsAlreadySynced: any = {};
    return this.contactEmailsData.map((item, index) => {
      let mailchimpEmailSync: MailchimpEmailSyncModel | undefined =
        this.mailchimpEmailSyncs.find(
          (x) => x.emailAddress.toLowerCase() === item.v.toLowerCase(),
        );
      if (mailchimpEmailSync) {
        if (mailchimpEmailSyncsAlreadySynced[mailchimpEmailSync.id]) {
          // already synced with same email from another data item, so don't sync again
          mailchimpEmailSync = undefined;
        } else {
          mailchimpEmailSyncsAlreadySynced[mailchimpEmailSync.id] = true;
        }
      }

      let label = item.l;
      if (mailchimpEmailSync) {
        if (mailchimpEmailSync.hasSyncError) {
          if (
            isPresent(mailchimpEmailSync.status) &&
            mailchimpEmailSync.status !== 'subscribed'
          ) {
            label = [
              label,
              `(${mailchimpEmailSync.status} from Mailchimp)`,
            ].join(' ');
          } else {
            label = [label, '(error syncing w Mailchimp)'].join(' ');
          }
        } else {
          label = [label, '(synced w Mailchimp)'].join(' ');
        }
      }
      return {
        label,
        data: item,
        dataIndex: index,
        mailchimpEmailSync,
      };
    });
  }

  get showEditIcon() {
    return this.abilities.hasAbility('edit_contact') && !this.editing;
  }

  createChangeset(
    item?: EmailItem,
    options: { syncWithMailchimp?: boolean } = {},
  ): EmailItemChangeset {
    return Changeset({
      label: item?.data.l,
      value: item?.data.v,
      syncWithMailchimp:
        item?.mailchimpEmailSync != null || options.syncWithMailchimp === true,
    }) as unknown as EmailItemChangeset;
  }

  @action
  handleEditClicked() {
    if (this.args.contactDetailRecordIsLoading) {
      this.waitingForRecordToLoad = true;
    } else {
      this.startEditing();
    }
  }

  startEditing() {
    this.editing = true;
    this.changesets = this.emailItems.map((x) => this.createChangeset(x));
    if (this.changesets.length === 0) {
      this.addEmail();
    }
  }

  @action
  handleCancelClicked() {
    this.editing = false;
    this.saveError = undefined;
  }

  @action
  addEmail() {
    let syncWithMailchimp = false;
    if (
      this.settings.mailchimpFundSyncSlim?.automaticallySyncFirstEmail &&
      this.changesets.length === 0
    ) {
      syncWithMailchimp = true;
    } else if (
      this.settings.mailchimpFundSyncSlim?.automaticallySyncAllEmails
    ) {
      syncWithMailchimp = true;
    }

    this.changesets = [
      ...this.changesets,
      this.createChangeset(undefined, { syncWithMailchimp }),
    ];
  }

  @action
  deleteEmail(index: number) {
    this.changesets.splice(index, 1);
    this.changesets = this.changesets.slice();
  }

  @action
  async save() {
    this.saving = true;
    this.changesets.forEach((x) => x.execute());
    if (this.args.contact.emails == null) {
      this.args.contact.emails = {};
    }

    let editEmail = this.store.createRecord('contact-edit-email');
    editEmail.contactId = this.args.contact.id;
    editEmail.editData = (this.changesets as EmailItemChangeset[])
      .filter((x) => isPresent(x.get('value')))
      .map((x) => {
        return {
          value: x.get('value')!.trim(),
          label: x.get('label')?.trim(),
          sync_with_mailchimp: x.get('syncWithMailchimp'),
        };
      });

    try {
      await editEmail.save();
      this.editing = false;
      this.args.contact.emails.data = editEmail.savedData;
      this.contactEmailsData = editEmail.savedData || [];
      this.mailchimpEmailSyncs = editEmail.mailchimpEmailSyncs.slice();
      this.saving = false;
    } catch (error) {
      this.editing = true;
      this.saving = false;
      this.saveError = error;
    }
  }
}
